import FormatValue from 'assets/components/formatValue/FormatValue';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { defaultDateFormat, localizeDate } from 'assets/utils/data/Date';
import { combineStrings } from 'assets/utils/data/String';
import React from 'react';
import { SalesContractInfoTab, useSalesContractProvider } from '../../Sales.Contract.Hooks';

const titleStyle: React.CSSProperties = { fontSize: Theme.FontSize.L, fontWeight: 'bold' };
export default function ContractInfoNavigation() {
  const { lang } = getLocales();
  const {
    pageRouter,
    selected,
    revisionsApi,
    commentsApi,
    subfeeDocs,
    contractStatusAuditLogListApi,
    permissions,
    hasAnyContractCommentPermission,
    contractCancellation,
    contractRefund,
  } = useSalesContractProvider();

  return (
    <>
      <div
        data-active={pageRouter.query.infoTab === SalesContractInfoTab.DOCUMENTS ? true : undefined}
        onClick={() => pageRouter.updateQuery({ ...pageRouter.query, infoTab: SalesContractInfoTab.DOCUMENTS })}
        title={lang.showMore}
      >
        <div style={titleStyle}>{lang.documents}</div>
        <br />
        {lang.autoFilledDocuments}: {(selected?.documents?.length ?? 0).toString()}
        <br />
        {lang.transferDocuments}: {(selected?.transferDocuments?.length ?? 0).toString()}
        <br />
        {lang.productTemplateDocuments}: {selected?.productDocuments?.length ?? '0'}
        <br />
        {lang.surchargeDocuments}: {subfeeDocs?.length ?? '0'}
        <br />
        {lang.refundDocuments}: {contractRefund?.documentationAggregate ? '1' : '0'}
        <br />
        {lang.additionalDocumentation}: {selected?.contractDocuments?.length ?? '0'}
      </div>
      <div
        data-active={pageRouter.query.infoTab === SalesContractInfoTab.REVISIONS ? true : undefined}
        onClick={() => pageRouter.updateQuery({ ...pageRouter.query, infoTab: SalesContractInfoTab.REVISIONS })}
        title={lang.showMore}
      >
        <div style={titleStyle}>{lang.revisions}</div>
        <br />
        {revisionsApi?.payload?.length ? (
          <>
            {revisionsApi?.payload?.length} {lang.revisions}
          </>
        ) : (
          lang.noInfo
        )}
      </div>
      <div
        data-active={pageRouter.query.infoTab === SalesContractInfoTab.INFO ? true : undefined}
        onClick={() => pageRouter.updateQuery({ ...pageRouter.query, infoTab: SalesContractInfoTab.INFO })}
        title={lang.showMore}
      >
        <div style={titleStyle}>{lang.info}</div>
        <div>
          {selected.product?.name || lang.unknownProduct}:{' '}
          <b>
            <FormatValue value={`${(selected.priceCAD || 0).toFixed(2)}$`} />
          </b>
        </div>
        {selected?.subfees &&
          selected?.subfees?.map((it) => (
            <div key={it.subfeeId}>
              {it.name}:{' '}
              <b>
                <FormatValue value={`$${(it.amount || 0).toFixed(2)}`} />
              </b>
            </div>
          ))}
        {selected?.contractEnhancements &&
          selected?.contractEnhancements?.map((it) => (
            <div key={it.productEnhancementId}>
              {it.productEnhancement?.name}:{' '}
              <b>
                <FormatValue value={`${(it.priceCAD || 0).toFixed(2)}$`} />
              </b>
            </div>
          ))}
        <div>
          {lang.appNumber}: <b>{selected.appNumber}</b>
        </div>
        <div>
          {lang.vin}: <b>{selected.vehicle.vin}</b>
        </div>
        <div>
          {lang.client}:{' '}
          <b>
            {selected.client.firstName} {selected.client.lastName}
          </b>
        </div>
        <div>
          {lang.dealer}: <b>{selected.dealer.name}</b>
        </div>
        <br />
        <div>
          {lang.status}:{' '}
          <b>{selected.statusCode ? lang[selected.statusCode]?.replace(/_/g, ' - ') : lang.noContractDefined}</b>
        </div>
      </div>
      {contractCancellation && (
        <div
          data-active={pageRouter.query.infoTab === SalesContractInfoTab.CANCELLATION ? true : undefined}
          onClick={() => pageRouter.updateQuery({ ...pageRouter.query, infoTab: SalesContractInfoTab.CANCELLATION })}
          title={lang.showMore}
        >
          <div style={titleStyle}>{lang.cancellationHistory}</div>
          <div>
            {lang.lastCancellationDate}:{' '}
            <b>{localizeDate(contractCancellation?.cancellationDateLocal, defaultDateFormat)}</b>
          </div>
          <div>
            {lang.lastCancellationReason}: <b>{contractCancellation?.reason}</b>
          </div>{' '}
          {contractCancellation?.odometerReading && (
            <div>
              {lang.lastOdometerReading}:{' '}
              <b>
                <FormatValue value={`${contractCancellation?.odometerReading || 0}km`} />
              </b>
            </div>
          )}
          {contractCancellation?.reasonDescription && (
            <div>
              {lang.lastCancellationReasonDescription}: <b>{contractCancellation?.reasonDescription}</b>
            </div>
          )}
          {contractCancellation?.refundDateAtLocal && (
            <div>
              {lang.lastRefundDate}: <b>{localizeDate(contractCancellation?.refundDateAtLocal, defaultDateFormat)}</b>
            </div>
          )}
        </div>
      )}
      {hasAnyContractCommentPermission && (
        <div
          data-active={pageRouter.query.infoTab === SalesContractInfoTab.NOTES ? true : undefined}
          onClick={() => pageRouter.updateQuery({ ...pageRouter.query, infoTab: SalesContractInfoTab.NOTES })}
          title={lang.showMore}
        >
          <div style={titleStyle}>{lang.notes}</div>
          <br />
          {lang.notes}: {commentsApi?.payload?.count ?? '0'}
        </div>
      )}
      {selected.clientHistory?.length > 0 && (
        <div
          data-active={pageRouter.query.infoTab === SalesContractInfoTab.CLIENT_HISTORY ? true : undefined}
          onClick={() => pageRouter.updateQuery({ ...pageRouter.query, infoTab: SalesContractInfoTab.CLIENT_HISTORY })}
          title={lang.showMore}
        >
          <div style={titleStyle}>{lang.transfers}</div>
          <br />
          {lang.clientHistory}: {selected.clientHistory?.length ?? '0'}
          <br />
        </div>
      )}
      {
        <div
          data-active={pageRouter.query.infoTab === SalesContractInfoTab.ALERTS ? true : undefined}
          onClick={() => pageRouter.updateQuery({ ...pageRouter.query, infoTab: SalesContractInfoTab.ALERTS })}
          title={lang.showMore}
        >
          <div style={titleStyle}>{lang.alertsAndNotifications}</div>
          <br />
          {lang.alertsAndNotifications}: {selected?.contractAlerts?.length ?? '0'}
          <br />
        </div>
      }
      {permissions?.VIEW_CONTRACT_LOGS && (
        <div
          data-active={pageRouter.query.infoTab === SalesContractInfoTab.STATUS_AUDIT_LOGS ? true : undefined}
          onClick={() =>
            pageRouter.updateQuery({ ...pageRouter.query, infoTab: SalesContractInfoTab.STATUS_AUDIT_LOGS })
          }
          title={lang.showMore}
        >
          <div style={titleStyle}>{combineStrings(' - ', lang.status, lang.changeLogs)}</div>
          <br />
          {lang.logs}: {contractStatusAuditLogListApi?.payload?.count ?? '0'}
          <br />
        </div>
      )}
    </>
  );
}
